import { ChangeDetectionStrategy, Component, Inject, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { cache } from '@common/util/cache.operator';
import { isEnumValue } from '@common/util/enum';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { ProjectChecklistProfile } from '@portal-core/profiles/models/project-checklist-profile.model';
import { ProfilesService } from '@portal-core/profiles/services/profiles.service';
import { ProjectChecklistProfileComponent } from '@portal-core/project-checklists/components/project-checklist-profile/project-checklist-profile.component';
import { ProjectChecklistProfileTab } from '@portal-core/project-checklists/enums/project-checklist-profile-tab.enum';
import { DialogBase } from '@portal-core/ui/dialog/util/dialog.base';
import { AutoUnsubscribe } from '@portal-core/util/auto-unsubscribe.decorator';
import { LoadingState } from '@portal-core/util/loading-state';
import { BehaviorSubject, first, Observable, switchMap } from 'rxjs';

export interface ChecklistProfileDialogData {
  checklistId: number;
  checklistProfileTab?: ProjectChecklistProfileTab;
}

@Component({
  selector: 'mc-project-checklist-profile-dialog',
  templateUrl: './project-checklist-profile-dialog.component.html',
  styleUrls: ['./project-checklist-profile-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
@AutoUnsubscribe()
export class ProjectChecklistProfileDialogComponent extends DialogBase implements OnInit {
  static DialogConfig: MatDialogConfig = {
    width: '90rem',
    height: '64rem'
  };

  @ViewChild(ProjectChecklistProfileComponent, { static: true }) checklistProfileComponent: ProjectChecklistProfileComponent;

  checklistProfile$: Observable<ProjectChecklistProfile>;
  checklistProfileTab: ProjectChecklistProfileTab;
  loadingState: LoadingState<string> = new LoadingState<string>();
  reload$: BehaviorSubject<void> = new BehaviorSubject(undefined);

  get requirePromptOnClose(): boolean {
    return this.checklistProfileComponent?.dirty;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ChecklistProfileDialogData,
    protected dialog: MatDialog,
    protected dialogRef: MatDialogRef<ProjectChecklistProfileDialogComponent>,
    private errorService: ErrorService,
    private profilesService: ProfilesService
  ) {
    super(dialog, dialogRef);
  }

  ngOnInit() {
    super.ngOnInit();

    this.loadingState.update(true);

    this.checklistProfile$ = this.reload$.pipe(
      switchMap(() => this.profilesService.getProjectChecklistProfile$(this.data.checklistId)),
      cache()
    );

    this.checklistProfile$.pipe(
      first()
    ).subscribe(() => {
      this.checklistProfileTab = this.getInitialTab(this.data.checklistProfileTab);
      this.loadingState.update(false);
      this.onLoaded();
    }, error => {
      this.loadingState.update(false, 'Unable to load the checklist profile.', this.errorService.getErrorMessages(error));
    });
  }

  onCancel() {
    this.closeDialog();
  }

  onNavigated() {
    this.closeDialog();
  }

  onSaved(projectChecklistProfileTab: ProjectChecklistProfileTab) {
    if (projectChecklistProfileTab === ProjectChecklistProfileTab.Delete) {
      this.closeDialog();
    } else {
      this.reload();
    }
  }

  private getInitialTab(checklistProfileTab: ProjectChecklistProfileTab): ProjectChecklistProfileTab {
    if (isEnumValue(ProjectChecklistProfileTab, checklistProfileTab)) {
      return checklistProfileTab;
    } else {
      return ProjectChecklistProfileTab.Overview;
    }
  }

  private reload() {
    this.reload$.next();
  }
}
